import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, DataTable, Spinner, Badge, Button, Drawer, Divider, Modal, FormField, FormSelect, PasswordField } from '../ui';
import { Users as UsersIcon, Unlock, Trash2, Wallet, DollarSign, TrendingUp, TrendingDown, Calendar, Mail, User, Clock, Plus, Edit2, Copy, RefreshCw } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';

const Users = () => {
  const { user: currentUser } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [checkedUsers, setCheckedUsers] = useState(new Set());
  const [showDrawer, setShowDrawer] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [userDetails, setUserDetails] = useState({
    portfolio: null,
    transactions: [],
    payouts: [],
    loading: false
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'viewer',
    invested_amount: '',
    roi_min: '',
    roi_max: '',
    startDate: '',
    aed_conversion_rate: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (userId) => {
    try {
      await api.patch(`/api/users/${userId}/unlock`);
      showSuccess('User unlocked successfully');
      fetchUsers();
    } catch (error) {
      showError('Failed to unlock user');
    }
  };

  const handleDelete = async (userId) => {
    const confirmed = await confirm({
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/users/${userId}`);
      showSuccess('User deleted successfully');
      fetchUsers();
    } catch (error) {
      showError('Failed to delete user');
    }
  };

  const handleBulkDelete = async () => {
    if (checkedUsers.size === 0) {
      showError('Please select at least one user');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Multiple Users',
      message: `Are you sure you want to delete ${checkedUsers.size} user(s)? This action cannot be undone.`,
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const deletePromises = Array.from(checkedUsers).map(id => api.delete(`/api/users/${id}`));
      await Promise.all(deletePromises);
      showSuccess(`${checkedUsers.size} user(s) deleted successfully`);
      setCheckedUsers(new Set());
      fetchUsers();
    } catch (error) {
      showError('Failed to delete one or more users');
    }
  };

  const toggleUserCheck = (userId) => {
    const newSet = new Set(checkedUsers);
    if (newSet.has(userId)) {
      newSet.delete(userId);
    } else {
      newSet.add(userId);
    }
    setCheckedUsers(newSet);
  };

  const toggleAllUsersCheck = () => {
    if (checkedUsers.size === users.length) {
      setCheckedUsers(new Set());
    } else {
      setCheckedUsers(new Set(users.map(u => u._id)));
    }
  };

  const fetchUserDetails = async (userId) => {
    setUserDetails({ portfolio: null, transactions: [], payouts: [], loading: true });
    try {
      const [portfolioRes, transactionsRes, payoutsRes] = await Promise.all([
        api.get(`/api/users/${userId}/portfolio`).catch(() => ({ data: { portfolio: null } })),
        api.get(`/api/users/${userId}/transactions?limit=50`).catch(() => ({ data: { transactions: [] } })),
        api.get(`/api/payouts?userId=${userId}`).catch(() => ({ data: { payouts: [] } }))
      ]);

      setUserDetails({
        portfolio: portfolioRes.data.portfolio,
        transactions: transactionsRes.data.transactions || [],
        payouts: payoutsRes.data.payouts || [],
        loading: false
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      setUserDetails({ portfolio: null, transactions: [], payouts: [], loading: false });
    }
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setShowDrawer(true);
    await fetchUserDetails(user._id);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'viewer',
      invested_amount: '',
      roi_min: '',
      roi_max: '',
      startDate: '',
      aed_conversion_rate: ''
    });
    setFormErrors({});
  };

  const handleCreateClick = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email || '',
      password: '', // Don't pre-fill password
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role || 'viewer',
      invested_amount: user.invested_amount || '',
      roi_min: user.roi_min || '',
      roi_max: user.roi_max || '',
      startDate: user.startDate ? new Date(user.startDate).toISOString().split('T')[0] : '',
      aed_conversion_rate: user.aed_conversion_rate || ''
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const validateForm = (isEdit = false) => {
    const errors = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!isEdit && !formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (formData.password && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (formData.role === 'investor') {
      if (formData.invested_amount && isNaN(parseFloat(formData.invested_amount))) {
        errors.invested_amount = 'Invested amount must be a number';
      }
      if (formData.roi_min && isNaN(parseFloat(formData.roi_min))) {
        errors.roi_min = 'ROI min must be a number';
      }
      if (formData.roi_max && isNaN(parseFloat(formData.roi_max))) {
        errors.roi_max = 'ROI max must be a number';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!validateForm(false)) {
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role,
      };

      if (formData.role === 'investor') {
        if (formData.invested_amount) payload.invested_amount = parseFloat(formData.invested_amount);
        if (formData.roi_min) payload.roi_min = parseFloat(formData.roi_min);
        if (formData.roi_max) payload.roi_max = parseFloat(formData.roi_max);
        if (formData.aed_conversion_rate) payload.aed_conversion_rate = parseFloat(formData.aed_conversion_rate);
      }

      if (formData.startDate) {
        payload.startDate = new Date(formData.startDate).toISOString();
      }

      await api.post('/api/users', payload);
      showSuccess('User created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create user';
      showError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    if (!validateForm(true)) {
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role,
      };

      // Only include password if it's provided
      if (formData.password) {
        payload.password = formData.password;
      }

      if (formData.role === 'investor') {
        if (formData.invested_amount) payload.invested_amount = parseFloat(formData.invested_amount);
        if (formData.roi_min) payload.roi_min = parseFloat(formData.roi_min);
        if (formData.roi_max) payload.roi_max = parseFloat(formData.roi_max);
        if (formData.aed_conversion_rate) payload.aed_conversion_rate = parseFloat(formData.aed_conversion_rate);
      }

      if (formData.startDate) {
        payload.startDate = new Date(formData.startDate).toISOString();
      }

      await api.put(`/api/users/${editingUser._id}`, payload);
      showSuccess('User updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update user';
      showError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Generate random password that meets requirements (min 8 chars, uppercase, lowercase, number)
  const generatePassword = () => {
    const length = 12;
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*';
    const allChars = lowercase + uppercase + numbers + special;

    // Ensure password has at least one of each required type
    let password = '';
    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));

    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }

    // Shuffle the password to avoid predictable pattern
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData(prev => ({ ...prev, password: newPassword }));
    // Clear password error if any
    if (formErrors.password) {
      setFormErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handleCopyPassword = async () => {
    if (!formData.password) {
      showError('No password to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(formData.password);
      showSuccess('Password copied to clipboard');
    } catch (error) {
      showError('Failed to copy password');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const getStatusBadge = (user) => {
    if (user.isLocked || (user.lockUntil && new Date(user.lockUntil) > new Date())) {
      return <Badge variant="error">Locked</Badge>;
    }
    if (user.active === false) {
      return <Badge variant="default">Inactive</Badge>;
    }
    return <Badge variant="success">Active</Badge>;
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'error',
      investor: 'success',
      manager: 'primary',
      staff: 'warning',
      broker: 'info',
      trader: 'primary',
      viewer: 'default',
    };
    return <Badge variant={variants[role] || 'default'}>{role}</Badge>;
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (user) => (
        <button
          onClick={() => handleUserClick(user)}
          className="flex items-center gap-3 w-full text-left hover:opacity-80 transition-opacity"
        >
          <div className="w-10 h-10 rounded-full bg-primary-600 dark:bg-primary-700 flex items-center justify-center text-white font-medium flex-shrink-0">
            {user.firstName?.[0]}{user.lastName?.[0]}
          </div>
          <span className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:underline transition-colors">
            {user.firstName} {user.lastName}
          </span>
        </button>
      ),
    },
    { key: 'email', label: 'Email', render: (user) => <span className="text-primary-600 dark:text-primary-400">{user.email}</span> },
    { key: 'role', label: 'Role', render: (user) => getRoleBadge(user.role) },
    ...(currentUser?.role === 'admin' ? [
      {
        key: 'invested',
        label: 'Invested Amount',
        render: (user) => user.role === 'investor' ? `$${(user.invested_amount || 0).toLocaleString()} USDT` : <span className="text-primary-400">-</span>,
      },
      {
        key: 'balance',
        label: 'Balance',
        render: (user) => user.role === 'investor' ? (
          <span className="font-semibold text-success-600 dark:text-success-400">
            ${(user.balance_usdt || 0).toLocaleString()} USDT
          </span>
        ) : <span className="text-primary-400">-</span>,
      },
      {
        key: 'roi',
        label: 'ROI Range',
        render: (user) => user.role === 'investor' && user.roi_min !== undefined && user.roi_max !== undefined
          ? `${user.roi_min}% - ${user.roi_max}%`
          : <span className="text-primary-400">-</span>,
      },
      {
        key: 'payout',
        label: 'Payout Date',
        render: (user) => user.role === 'investor' && user.payout_date
          ? new Date(user.payout_date).toISOString().split('T')[0]
          : <span className="text-primary-400">-</span>,
      },
    ] : []),
    { key: 'status', label: 'Status', render: (user) => getStatusBadge(user) },
    {
      key: 'lastLogin',
      label: 'Last Login',
      render: (user) => <span className="text-primary-600 dark:text-primary-400 text-sm">{user.lastLogin ? `${new Date(user.lastLogin).toISOString().split('T')[0]} ${new Date(user.lastLogin).toLocaleTimeString()}` : 'Never'}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (user) => (
        <div className="flex items-center justify-center gap-2">
          {currentUser?.role === 'admin' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditClick(user)}
              leftIcon={<Edit2 size={16} />}
              className="text-primary-600 dark:text-primary-400"
            >
              Edit
            </Button>
          )}
          {(user.isLocked || (user.lockUntil && new Date(user.lockUntil) > new Date())) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleUnlock(user._id)}
              leftIcon={<Unlock size={16} />}
              className="text-success-600 dark:text-success-400"
            >
              Unlock
            </Button>
          )}
          {user._id !== currentUser?.id && currentUser?.role === 'admin' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(user._id)}
              leftIcon={<Trash2 size={16} />}
              className="text-error-600 dark:text-error-400"
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <UsersIcon className="text-primary-600 dark:text-primary-400" size={32} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
              Users
            </h1>
            <p className="text-primary-600 dark:text-primary-400 mt-1 text-sm sm:text-base">
              Manage user accounts and permissions
            </p>
          </div>
        </div>
        {currentUser?.role === 'admin' && (
          <Button
            variant="primary"
            onClick={handleCreateClick}
            leftIcon={<Plus size={20} />}
          >
            Create User
          </Button>
        )}
      </div>

      <Card className="p-6">
        {checkedUsers.size > 0 && (
          <div className="mb-4 p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-700 rounded-lg flex items-center justify-between">
            <span className="text-warning-900 dark:text-warning-50 font-medium">
              {checkedUsers.size} selected
            </span>
            <Button
              onClick={handleBulkDelete}
              variant="danger"
              size="sm"
            >
              Delete Selected
            </Button>
          </div>
        )}
        <DataTable
          columns={[
            {
              key: 'checkbox',
              label: (
                <input
                  type="checkbox"
                  checked={checkedUsers.size === users.length && users.length > 0}
                  onChange={toggleAllUsersCheck}
                  className="w-4 h-4"
                />
              ),
              render: (user) => (
                <input
                  type="checkbox"
                  checked={checkedUsers.has(user._id)}
                  onChange={() => toggleUserCheck(user._id)}
                  className="w-4 h-4"
                />
              ),
            },
            ...columns
          ]}
          data={users}
          striped
          hover
          exportFileName="users"
          searchableFields={['firstName', 'lastName', 'email', 'role']}
        />
      </Card>

      {/* User Details Drawer */}
      <Drawer
        isOpen={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setSelectedUser(null);
          setUserDetails({ portfolio: null, transactions: [], payouts: [], loading: false });
        }}
        title={selectedUser ? (
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-600 dark:bg-primary-700 flex items-center justify-center text-white font-semibold text-lg">
                {selectedUser.firstName?.[0]}{selectedUser.lastName?.[0]}
              </div>
              <div>
                <div className="text-xl font-semibold text-primary-900 dark:text-primary-50">
                  {selectedUser.firstName} {selectedUser.lastName}
                </div>
                <div className="text-sm text-primary-600 dark:text-primary-400 mt-0.5">
                  {selectedUser.email}
                </div>
              </div>
            </div>
          </div>
        ) : 'User Details'}
        size="lg"
        position="right"
      >
        {selectedUser && (
          <div className="space-y-6">
            {/* Account Information */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Account Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                  <Mail className="text-primary-400 dark:text-primary-600" size={20} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Email</p>
                    <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                      {selectedUser.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                  <User className="text-primary-400 dark:text-primary-600" size={20} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Role</p>
                    <div className="mt-1">
                      {getRoleBadge(selectedUser.role)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                  <Clock className="text-primary-400 dark:text-primary-600" size={20} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Status</p>
                    <div className="mt-1">
                      {getStatusBadge(selectedUser)}
                    </div>
                  </div>
                </div>
                {selectedUser.lastLogin && (
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <Calendar className="text-primary-400 dark:text-primary-600" size={20} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Last Login</p>
                      <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                        {new Date(selectedUser.lastLogin).toISOString().split('T')[0]} {new Date(selectedUser.lastLogin).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Investor-Specific Information */}
            {selectedUser.role === 'investor' && (
              <>
                <Divider />
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                    Investment Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Card className="p-4 bg-primary-50 dark:bg-primary-900/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                            Invested Amount
                          </p>
                          <p className="text-xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                            ${(selectedUser.invested_amount || 0).toLocaleString()} USDT
                          </p>
                        </div>
                        <Wallet className="text-primary-400 dark:text-primary-600" size={24} />
                      </div>
                    </Card>
                    <Card className="p-4 bg-success-50 dark:bg-success-900/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide">
                            Balance
                          </p>
                          <p className="text-xl font-bold text-success-900 dark:text-success-50 mt-1">
                            ${(selectedUser.balance_usdt || 0).toLocaleString()} USDT
                          </p>
                        </div>
                        <DollarSign className="text-success-400 dark:text-success-600" size={24} />
                      </div>
                    </Card>
                    {selectedUser.roi_min !== undefined && selectedUser.roi_max !== undefined && (
                      <Card className="p-4 bg-accent-50 dark:bg-accent-900/20">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-accent-600 dark:text-accent-400 uppercase tracking-wide">
                              ROI Range
                            </p>
                            <p className="text-xl font-bold text-accent-900 dark:text-accent-50 mt-1">
                              {selectedUser.roi_min}% - {selectedUser.roi_max}%
                            </p>
                          </div>
                          <TrendingUp className="text-accent-400 dark:text-accent-600" size={24} />
                        </div>
                      </Card>
                    )}
                    {selectedUser.payout_date && (
                      <Card className="p-4 bg-secondary-50 dark:bg-secondary-900/20">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-secondary-600 dark:text-secondary-400 uppercase tracking-wide">
                              Next Payout Date
                            </p>
                            <p className="text-xl font-bold text-secondary-900 dark:text-secondary-50 mt-1">
                              {new Date(selectedUser.payout_date).toISOString().split('T')[0]}
                            </p>
                          </div>
                          <Calendar className="text-secondary-400 dark:text-secondary-600" size={24} />
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Portfolio Summary */}
            {userDetails.portfolio && (
              <>
                <Divider />
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                    Portfolio Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4 bg-primary-50 dark:bg-primary-900/50">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                        Total Value
                      </p>
                      <p className="text-xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                        ${(userDetails.portfolio.totalValue || 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className="p-4 bg-primary-50 dark:bg-primary-900/50">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                        Total Invested
                      </p>
                      <p className="text-xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                        ${(userDetails.portfolio.totalInvested || 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className="p-4 bg-success-50 dark:bg-success-900/20">
                      <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide">
                        Gain/Loss
                      </p>
                      <p className={`text-xl font-bold mt-1 ${(userDetails.portfolio.totalGainLoss || 0) >= 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                        }`}>
                        ${(userDetails.portfolio.totalGainLoss || 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className="p-4 bg-success-50 dark:bg-success-900/20">
                      <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide">
                        Gain/Loss %
                      </p>
                      <p className={`text-xl font-bold mt-1 ${(userDetails.portfolio.totalGainLossPercent || 0) >= 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                        }`}>
                        {(userDetails.portfolio.totalGainLossPercent || 0).toFixed(2)}%
                      </p>
                    </Card>
                  </div>
                </div>
              </>
            )}

            <Divider />

            {/* Activity Timeline */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Activity Timeline
              </h3>
              {userDetails.loading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="sm" />
                </div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    // Combine transactions and payouts into a timeline
                    const timelineItems = [
                      ...userDetails.transactions.map(tx => ({
                        type: 'transaction',
                        date: tx.date || tx.createdAt,
                        data: tx,
                        label: tx.transactionType === 'deposit' ? 'Investment' : 'Withdrawal',
                        amount: tx.amount,
                        isPositive: tx.transactionType === 'deposit',
                        currency: tx.currency || 'USDT'
                      })),
                      ...userDetails.payouts.map(payout => ({
                        type: 'payout',
                        date: payout.date || payout.createdAt,
                        data: payout,
                        label: 'Payout',
                        amount: payout.amt || payout.amount,
                        isPositive: true,
                        currency: 'USDT'
                      }))
                    ].sort((a, b) => new Date(b.date) - new Date(a.date));

                    if (timelineItems.length === 0) {
                      return (
                        <div className="text-center py-8 border-2 border-dashed border-primary-200 dark:border-primary-700 rounded-lg">
                          <Calendar size={32} className="mx-auto text-primary-400 dark:text-primary-600 mb-2" />
                          <p className="text-sm text-primary-600 dark:text-primary-400">
                            No activity yet
                          </p>
                        </div>
                      );
                    }

                    return timelineItems.map((item, index) => (
                      <div key={index} className="relative pl-8 pb-4">
                        {/* Timeline line */}
                        {index < timelineItems.length - 1 && (
                          <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-primary-200 dark:bg-primary-700" />
                        )}
                        {/* Timeline dot */}
                        <div className={`absolute left-0 top-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center ${item.isPositive
                            ? 'bg-success-100 dark:bg-success-900/30 border-success-400 dark:border-success-600'
                            : 'bg-error-100 dark:bg-error-900/30 border-error-400 dark:border-error-600'
                          }`}>
                          {item.type === 'transaction' ? (
                            item.data.transactionType === 'deposit' ? (
                              <TrendingUp size={12} className="text-success-600 dark:text-success-400" />
                            ) : (
                              <TrendingDown size={12} className="text-error-600 dark:text-error-400" />
                            )
                          ) : (
                            <DollarSign size={12} className="text-success-600 dark:text-success-400" />
                          )}
                        </div>
                        {/* Content */}
                        <div className="bg-primary-50 dark:bg-primary-900/50 rounded-lg p-3 border border-primary-200 dark:border-primary-700">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant={item.isPositive ? 'success' : 'error'} className="text-xs">
                                  {item.label}
                                </Badge>
                                <span className="text-xs text-primary-500 dark:text-primary-400">
                                  {new Date(item.date).toISOString().split('T')[0]}
                                </span>
                              </div>
                              {item.data.description && (
                                <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                                  {item.data.description}
                                </p>
                              )}
                              {item.type === 'payout' && item.data.admin_name && (
                                <p className="text-xs text-primary-500 dark:text-primary-400 mt-1">
                                  Processed by: {item.data.admin_name}
                                </p>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <p className={`text-lg font-bold ${item.isPositive
                                  ? 'text-success-600 dark:text-success-400'
                                  : 'text-error-600 dark:text-error-400'
                                }`}>
                                {item.isPositive ? '+' : '-'}${item.amount?.toLocaleString() || 0}
                              </p>
                              <p className="text-xs text-primary-500 dark:text-primary-400 mt-0.5">
                                {item.currency}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Account Metadata */}
            <Divider />
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Account Metadata
              </h3>
              <div className="space-y-2">
                {selectedUser.createdAt && (
                  <div className="flex items-center justify-between py-2 border-b border-primary-200 dark:border-primary-700">
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      Created At
                    </span>
                    <span className="text-sm text-primary-900 dark:text-primary-50">
                      {new Date(selectedUser.createdAt).toISOString().split('T')[0]} {new Date(selectedUser.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                )}
                {selectedUser.updatedAt && (
                  <div className="flex items-center justify-between py-2 border-b border-primary-200 dark:border-primary-700">
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      Last Updated
                    </span>
                    <span className="text-sm text-primary-900 dark:text-primary-50">
                      {new Date(selectedUser.updatedAt).toISOString().split('T')[0]} {new Date(selectedUser.updatedAt).toLocaleTimeString()}
                    </span>
                  </div>
                )}
                {selectedUser.role === 'investor' && selectedUser.aed_conversion_rate && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      AED Conversion Rate
                    </span>
                    <span className="text-sm text-primary-900 dark:text-primary-50">
                      {selectedUser.aed_conversion_rate}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create New User"
        size="lg"
      >
        <form onSubmit={handleCreateUser} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              error={formErrors.firstName}
              required
              sanitizeType="text"
            />
            <FormField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              error={formErrors.lastName}
              required
              sanitizeType="text"
            />
          </div>

          <FormField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={formErrors.email}
            required
            sanitizeType="email"
          />

          <div className="space-y-2">
            <PasswordField
              label="Password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              error={formErrors.password}
              required
              helperText="Must be at least 8 characters with uppercase, lowercase, and number"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleGeneratePassword}
                leftIcon={<RefreshCw size={16} />}
                className="flex-1"
              >
                Generate Password
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleCopyPassword}
                leftIcon={<Copy size={16} />}
                disabled={!formData.password}
                className="flex-1"
              >
                Copy
              </Button>
            </div>
          </div>

          <FormSelect
            label="Role"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            required
          >
            <option value="viewer">Viewer</option>
            <option value="investor">Investor</option>
            <option value="broker">Broker</option>
            <option value="trader">Trader</option>
            <option value="manager">Manager</option>
            <option value="staff">Staff</option>
            <option value="admin">Admin</option>
          </FormSelect>

          {formData.role === 'investor' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  label="Invested Amount (USDT)"
                  name="invested_amount"
                  type="number"
                  value={formData.invested_amount}
                  onChange={handleInputChange}
                  error={formErrors.invested_amount}
                  sanitizeType="number"
                  step="0.01"
                />
                <FormField
                  label="AED Conversion Rate"
                  name="aed_conversion_rate"
                  type="number"
                  value={formData.aed_conversion_rate}
                  onChange={handleInputChange}
                  sanitizeType="number"
                  step="0.01"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  label="ROI Min (%)"
                  name="roi_min"
                  type="number"
                  value={formData.roi_min}
                  onChange={handleInputChange}
                  error={formErrors.roi_min}
                  sanitizeType="number"
                  step="0.01"
                />
                <FormField
                  label="ROI Max (%)"
                  name="roi_max"
                  type="number"
                  value={formData.roi_max}
                  onChange={handleInputChange}
                  error={formErrors.roi_max}
                  sanitizeType="number"
                  step="0.01"
                />
              </div>
            </>
          )}

          <FormField
            label="Start Date"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={handleInputChange}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-primary-200 dark:border-primary-700">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              disabled={formLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={formLoading}
            >
              Create User
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingUser(null);
          resetForm();
        }}
        title="Edit User"
        size="lg"
      >
        {editingUser && (
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                error={formErrors.firstName}
                required
                sanitizeType="text"
              />
              <FormField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                error={formErrors.lastName}
                required
                sanitizeType="text"
              />
            </div>

            <FormField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={formErrors.email}
              required
              disabled
              helperText="Email cannot be changed"
              sanitizeType="email"
            />

            <div className="space-y-2">
              <PasswordField
                label="Password (leave blank to keep current)"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                error={formErrors.password}
                helperText="Leave blank to keep current password, or enter new password"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleGeneratePassword}
                  leftIcon={<RefreshCw size={16} />}
                  className="flex-1"
                >
                  Generate Password
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyPassword}
                  leftIcon={<Copy size={16} />}
                  disabled={!formData.password}
                  className="flex-1"
                >
                  Copy
                </Button>
              </div>
            </div>

            <FormSelect
              label="Role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              required
            >
              <option value="viewer">Viewer</option>
              <option value="investor">Investor</option>
              <option value="broker">Broker</option>
              <option value="trader">Trader</option>
              <option value="manager">Manager</option>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </FormSelect>

            {formData.role === 'investor' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="Invested Amount (USDT)"
                    name="invested_amount"
                    type="number"
                    value={formData.invested_amount}
                    onChange={handleInputChange}
                    error={formErrors.invested_amount}
                    sanitizeType="number"
                    step="0.01"
                  />
                  <FormField
                    label="AED Conversion Rate"
                    name="aed_conversion_rate"
                    type="number"
                    value={formData.aed_conversion_rate}
                    onChange={handleInputChange}
                    sanitizeType="number"
                    step="0.01"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="ROI Min (%)"
                    name="roi_min"
                    type="number"
                    value={formData.roi_min}
                    onChange={handleInputChange}
                    error={formErrors.roi_min}
                    sanitizeType="number"
                    step="0.01"
                  />
                  <FormField
                    label="ROI Max (%)"
                    name="roi_max"
                    type="number"
                    value={formData.roi_max}
                    onChange={handleInputChange}
                    error={formErrors.roi_max}
                    sanitizeType="number"
                    step="0.01"
                  />
                </div>
              </>
            )}

            <FormField
              label="Start Date"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange}
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-primary-200 dark:border-primary-700">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                  resetForm();
                }}
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={formLoading}
              >
                Update User
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Users;

